import type { GgxTourDefinition } from './tour-types';

export const HOWTO_11: GgxTourDefinition = {
    key: '[HOWTO-11] Organizing, Sharing, and Collaborating in the Workspace',
    route: '/home',
    estimatedMinutes: 3,
    prerequisite: {
        target: '[test-id="home-page-cards"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'One workspace for every team',
        subtitle:
            'Building a GenAI use case is a team sport. Data science builds the pipeline, ' +
            'risk and compliance review it, and business teams are the ones who know whether ' +
            'the answers are right. GGX puts all of them in one secure workspace, so a ' +
            'pipeline can be shared, tested and improved without files going back and forth. ' +
            'This short tour shows you how that works.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen how one workspace brings the whole team together: pipelines are ' +
            'shared instead of exported, business teams test them in the same place they were ' +
            'built, feedback flows straight back to the people making changes, and every ' +
            'change and decision is tracked along the way. Try it now: share one of the ' +
            'example pipelines with a teammate and ask them to put it through its paces. The ' +
            'Live Testing a pipeline tour shows exactly what they will see.',
        learned: [
            'Pipelines are **shared, not exported**.',
            'Business teams **test in the same place** they were built.',
            'Every **change and decision is tracked** along the way.',
        ],
    },
    chapters: [
        {
            name: 'The idea',
            blurb: 'Why sharing beats sending',
            icon: 'grid',
            steps: [2],
        },
        {
            name: 'Watch it done',
            blurb: 'Organizing, sharing and collaborating in the workspace',
            icon: 'pulse',
            steps: [3],
        },
    ],
    steps: [
        {
            title: 'Share the pipeline, not a copy of it',
            whatHappens:
                'next, a short video walks through organizing and sharing pipelines with your ' +
                'teams.',
            icon: 'grid',
            description:
                'Most teams collaborate by sending things around: a notebook here, a screenshot ' +
                'there, a spreadsheet of test results by email. Each copy drifts from the ' +
                'original, and nobody can say which version was tested. GGX works the other way. ' +
                'The pipeline stays in one place, and you <strong>share</strong> it. Data ' +
                'science keeps building, business teams open the same pipeline and ' +
                '<strong>test it live</strong>, their feedback lands right where the changes ' +
                'are made, and every edit and decision is <strong>tracked</strong> along the ' +
                'way. Everyone looks at the same thing, so the conversation is about the ' +
                'answers, not about which file is current.',
        },
        {
            title: 'Watch it done, then try it yourself',
            whatHappens: 'the tour wraps up with a summary and a pointer to the live testing tour.',
            icon: 'pulse',
            description:
                'This short video shows how to organize your pipelines, share them with ' +
                'teammates and business teams, and keep the feedback loop running in one ' +
                'place. Watch it once, then share one of the example pipelines with someone on ' +
                'your team.',
            video: {
                src: 'https://genguardx.ai/tours/howto-11-demo.mp4',
                title: 'Organizing, Sharing, and Collaboration in the Workspace',
            },
        },
    ],
};
